import type { ChangeEvent } from 'react';

import type { GetWebcastsQueryVariables, Webcast } from '../../../generated/graphql-manager';
import type { EventsView } from '../../providers/ui-state-provider/ui-state-provider';

import { NetworkStatus } from '@apollo/client';
import {
  GridIcon,
  Heading,
  Paragraph,
  PlusIcon,
  PrimaryButton,
  SecondaryButton,
  Spinner,
  Tooltip,
  UnorderedListIcon,
  classNames,
  usePrevious,
} from '@movingimage-evp/mi-ui-component-library';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router';

import { ArchiveWebcastModal } from './archive-webcast';
import { DeleteWebcastModal } from './delete-webcast';
import { DuplicateWebcastModal, DuplicationProgressIndicator } from './duplicate-webcast';
import { UnarchiveWebcastModal } from './unarchive-webcast';
import { GridView, ListView } from './views';
import { ErrorPage } from '../../../components/error-page';
import { State, StateFilter, useGetWebcastsLazyQuery } from '../../../generated/graphql-manager';
import { useAbsoluteRoutes } from '../../../routes';
import { Filter } from '../../components/filter';
import { Search } from '../../components/search';
import { useCurrentUser } from '../../hooks/current-user';
import { useUserPermissions } from '../../hooks/user-permissions';
import { NUMBER_OF_EVENTS, useUiState } from '../../providers/ui-state-provider/ui-state-provider';

import accessDeniedOperatorImage from '../../../assets/access-denied-operator.svg';

import styles from './webcasts.module.css';
import managerStyles from '../../manager.module.css';

const PROGRESS_INDICATOR_TIMEOUT = 5000;
export const ONDEMAND_STATE = 'ONDEMAND' as State;
export const ARCHIVED_STATE = 'ARCHIVED' as State;

export function WebcastsPage() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { lsproId, userId } = useCurrentUser();
  const { isEventCreationAllowed, isAccessToArchivedEventsAllowed } = useUserPermissions();
  const previousLsproId = usePrevious(lsproId);
  const routes = useAbsoluteRoutes();
  const { getNumberOfEvents, getEventsView, getEventsFilter, getEventsSorting, getEventsSearch, saveUiState } =
    useUiState();

  const isArchivedWebcastsPage = pathname.includes(routes.archived);

  const FILTERS = [
    { value: StateFilter.PLANNED, label: t('manager.webcasts.filters.planned') },
    { value: StateFilter.LIVE, label: t('manager.webcasts.filters.live') },
    { value: StateFilter.ENDED, label: t('manager.webcasts.filters.ended') },
    { value: StateFilter.ON_DEMAND, label: t('manager.webcasts.filters.onDemand') },
    { value: userId, label: t('manager.webcasts.filters.myEvents') },
  ];

  const ARCHIVED_FILTERS = [{ value: userId, label: t('manager.webcasts.filters.myEvents') }];

  const [webcastView, setWebcastView] = useState(getEventsView());
  const [search, setSearch] = useState(getEventsSearch());
  const [temporarySearch, setTemporarySearch] = useState(getEventsSearch());

  const [webcastIdToArchive, setWebcastIdToArchive] = useState<Webcast['id']>('');
  const [webcastIdToUnarchive, setWebcastIdToUnarchive] = useState<Webcast['id']>('');
  const [webcastIdToDelete, setWebcastIdToDelete] = useState<Webcast['id']>('');
  const [webcastIdToDuplicate, setWebcastIdToDuplicate] = useState<Webcast['id']>('');
  const [webcastNameToDuplicate, setWebcastNameToDuplicate] = useState<Webcast['name']>('');
  const [duplicatingInProgressWebcastId, setDuplicatingInProgressWebcastId] = useState<Webcast['id']>('');
  const [isProgressIndicatorVisible, setIsProgressIndicatorVisible] = useState(false);
  const [orderBy, setOrderBy] = useState(getEventsSorting());
  const [chosenFilters, setChosenFilters] = useState(getEventsFilter());

  const stateFilters = chosenFilters.filter((filter) => Object.values(StateFilter).includes(filter as StateFilter));
  const usersFilters = chosenFilters.filter((filter) => !Object.values(StateFilter).includes(filter as StateFilter));

  const [getWebcasts, getWebcastsResponse] = useGetWebcastsLazyQuery();

  const isRefetching = getWebcastsResponse.networkStatus === NetworkStatus.fetchMore;
  const isFetching = getWebcastsResponse.networkStatus === NetworkStatus.loading;

  const timeoutRef = useRef<number>(undefined);
  const errorTimeoutRef = useRef<number>(undefined);
  const searchTimeoutRef = useRef(0);
  const isShowMoreEventsAvailable = getWebcastsResponse.data?.webcasts.pageInfo.hasNextPage;
  const edges = getWebcastsResponse.data?.webcasts.edges.map(({ node }) => node) || [];
  const webcasts = edges.map((webcast) => {
    const vodEnabled = webcast.streaming?.vod?.enabled;

    return {
      ...webcast,
      state: vodEnabled && webcast.state !== State.ARCHIVED ? ONDEMAND_STATE : webcast.state,
    };
  }) as Webcast[];

  useEffect(() => {
    if (!lsproId) return;

    const webcastsVariables: GetWebcastsQueryVariables = {
      lsproId,
      orderBy,
      first: getNumberOfEvents(),
      searchTerm: search,
      filterBy: {},
    };

    if (usersFilters.length > 0) {
      webcastsVariables.filterBy = {
        ...webcastsVariables.filterBy,
        users: usersFilters,
      };
    }

    if (stateFilters.length > 0) {
      webcastsVariables.filterBy = {
        ...webcastsVariables.filterBy,
        states: stateFilters as StateFilter[],
      };
    }

    if (isArchivedWebcastsPage) {
      webcastsVariables.filterBy = {
        ...webcastsVariables.filterBy,
        states: [StateFilter.ARCHIVED],
      };
    }

    // Remove `users` or `states` if they are empty
    if (usersFilters.length === 0) delete webcastsVariables.filterBy?.users;
    if (stateFilters.length === 0 && !isArchivedWebcastsPage) delete webcastsVariables.filterBy?.states;

    getWebcasts({
      variables: { ...webcastsVariables },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
    });
    // We don't want to refetch when numberOfEvents changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getWebcasts, lsproId, orderBy, search, chosenFilters, isArchivedWebcastsPage]);

  useEffect(() => {
    if (lsproId !== previousLsproId) {
      getWebcastsResponse.refetch({ lsproId });
      handleCleanSearch();
      setChosenFilters([]);
    }
  }, [lsproId, previousLsproId, getWebcastsResponse]);

  useEffect(() => {
    if (isProgressIndicatorVisible && !duplicatingInProgressWebcastId) {
      timeoutRef.current = window.setTimeout(() => {
        setIsProgressIndicatorVisible(false);
      }, PROGRESS_INDICATOR_TIMEOUT);
    } else {
      clearTimeout(timeoutRef.current);
    }

    return () => {
      clearTimeout(timeoutRef.current);
      clearTimeout(errorTimeoutRef.current);
    };
  }, [duplicatingInProgressWebcastId, isProgressIndicatorVisible]);

  useEffect(() => {
    saveUiState({ key: 'eventsView', value: webcastView });
    saveUiState({ key: 'eventsSorting', value: orderBy });
    saveUiState({ key: 'eventsFilter', value: chosenFilters });
    saveUiState({ key: 'eventsSearch', value: search });
  }, [webcastView, orderBy, chosenFilters, search, saveUiState]);

  useEffect(() => {
    if (isArchivedWebcastsPage) {
      const hasStateFilters = chosenFilters.some((filter) =>
        Object.values(StateFilter).includes(filter as StateFilter)
      );

      if (hasStateFilters) {
        setChosenFilters((prevFilters) =>
          prevFilters.filter((filter) => !Object.values(StateFilter).includes(filter as StateFilter))
        );
      }
    }
  }, [chosenFilters, isArchivedWebcastsPage]);

  const duplicateWebcast = () => {
    setDuplicatingInProgressWebcastId(webcastIdToDuplicate);
    setWebcastIdToDuplicate('');
    setIsProgressIndicatorVisible(true);
  };

  const handleOnDuplicateWebcast = (id: Webcast['id'], name: Webcast['name']) => {
    setWebcastIdToDuplicate(id);
    setWebcastNameToDuplicate(name);
  };

  const handleWebcastDuplicatedError = () => {
    errorTimeoutRef.current = window.setTimeout(() => {
      setDuplicatingInProgressWebcastId('');
      setIsProgressIndicatorVisible(false);
    }, PROGRESS_INDICATOR_TIMEOUT);
  };

  const handleLoadMoreEvents = () => {
    if (isRefetching) return;

    getWebcastsResponse.fetchMore({
      variables: { after: getWebcastsResponse.data?.webcasts.pageInfo.endCursor, first: NUMBER_OF_EVENTS },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        const edges = [...prev.webcasts.edges, ...fetchMoreResult.webcasts.edges];

        saveUiState({ key: 'numberOfEvents', value: edges.length });

        return {
          webcasts: {
            ...fetchMoreResult.webcasts,
            edges,
          },
        };
      },
    });
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    clearTimeout(searchTimeoutRef.current);
    const searchValue = event.target.value;
    setTemporarySearch(searchValue);

    searchTimeoutRef.current = window.setTimeout(() => setSearch(searchValue), 500);
  };

  const handleCleanSearch = () => {
    setSearch('');
    setTemporarySearch('');
  };

  const handleFilter = (filter: string) => {
    if (chosenFilters.includes(filter)) {
      setChosenFilters(chosenFilters.filter((f) => f !== filter));
    } else {
      setChosenFilters([...chosenFilters, filter]);
    }
  };

  const handleEventViewButtonClick = (view: EventsView) => {
    setWebcastView(view);
    saveUiState({ key: 'eventsScrollPosition', value: 0 });
  };

  if (!isAccessToArchivedEventsAllowed && isArchivedWebcastsPage) {
    return (
      <ErrorPage
        data-testid="access-denied"
        title={t('common.accessDenied.title')}
        subTitle={t('common.accessDenied.subTitle')}
        image={accessDeniedOperatorImage}
        style={{ gridArea: 'section', height: 'unset', background: 'none' }}
      />
    );
  }

  return (
    <>
      <section className={classNames(managerStyles.section, styles.webcastsPage)}>
        <Heading>{isArchivedWebcastsPage ? t('manager.webcasts.archivedTitle') : t('manager.webcasts.title')}</Heading>

        <header style={{ flex: 'none' }}>
          <Search
            data-testid="webcasts-search"
            autoFocus
            placeholder={t('manager.webcasts.search.placeholder')}
            wrapperProps={{ className: styles.search }}
            value={temporarySearch}
            onChange={handleSearchChange}
            onClear={handleCleanSearch}
          />

          <button
            className={classNames(styles.viewSwitcher, webcastView === 'grid' && styles.active)}
            onClick={() => handleEventViewButtonClick('grid')}
            data-testid="grid-view-button"
          >
            <GridIcon />
            <Paragraph>{t('manager.webcasts.button.gridView')}</Paragraph>
          </button>

          <button
            className={classNames(styles.viewSwitcher, webcastView === 'list' && styles.active)}
            onClick={() => handleEventViewButtonClick('list')}
            data-testid="list-view-button"
          >
            <UnorderedListIcon />
            <Paragraph>{t('manager.webcasts.button.listView')}</Paragraph>
          </button>

          {!isArchivedWebcastsPage && (
            <Tooltip
              label={t('manager.webcastSetup.notEditableMessage.role')}
              hidden={isEventCreationAllowed}
              data-testid="create-new-event-button-tooltip"
            >
              <PrimaryButton
                component={NavLink}
                to={routes.webcastSetup_eventDetails}
                data-testid="create-new-event-button"
                disabled={!isEventCreationAllowed}
              >
                {t('manager.webcasts.newWebcastButton')}
                <PlusIcon />
              </PrimaryButton>
            </Tooltip>
          )}
        </header>

        <div className={styles.filters}>
          <Filter
            value=""
            data-testid="filter-all"
            closeIconNotVisible
            active={chosenFilters.length === 0}
            onClick={() => setChosenFilters([])}
          >
            {t('manager.webcasts.filters.all')}
          </Filter>

          {(isArchivedWebcastsPage ? ARCHIVED_FILTERS : FILTERS).map((filter) => (
            <Filter
              key={filter.value}
              data-testid={`filter-${filter.value}`}
              value={filter.value}
              active={chosenFilters.includes(filter.value)}
              onClick={() => handleFilter(filter.value)}
            >
              {filter.label}
            </Filter>
          ))}
        </div>

        {!isFetching && webcastView === 'grid' && (
          <GridView
            webcasts={webcasts}
            isRefetching={isRefetching}
            onArchiveWebcast={setWebcastIdToArchive}
            onDeleteWebcast={setWebcastIdToDelete}
            onDuplicateWebcast={handleOnDuplicateWebcast}
            onUnarchiveWebcast={setWebcastIdToUnarchive}
          />
        )}

        {!isFetching && webcastView === 'list' && (
          <ListView
            webcasts={webcasts}
            isRefetching={isRefetching}
            onArchiveWebcast={setWebcastIdToArchive}
            onDeleteWebcast={setWebcastIdToDelete}
            onDuplicateWebcast={handleOnDuplicateWebcast}
            onUnarchiveWebcast={setWebcastIdToUnarchive}
            orderBy={orderBy}
            onSort={setOrderBy}
          />
        )}

        {isFetching && <Spinner size={40} style={{ margin: 'auto' }} />}

        {!isFetching && isShowMoreEventsAvailable && (
          <SecondaryButton
            data-testid="load-more-events-button"
            disabled={isRefetching}
            onClick={handleLoadMoreEvents}
            style={{ margin: '0 auto' }}
          >
            {isRefetching ? <Spinner /> : t('manager.webcasts.moreWebcastsButton')}
          </SecondaryButton>
        )}
      </section>

      <ArchiveWebcastModal
        webcastId={webcastIdToArchive}
        isOpen={webcastIdToArchive !== ''}
        onClose={() => setWebcastIdToArchive('')}
      />

      <UnarchiveWebcastModal
        webcastId={webcastIdToUnarchive}
        isOpen={webcastIdToUnarchive !== ''}
        onClose={() => setWebcastIdToUnarchive('')}
      />

      <DeleteWebcastModal
        webcastId={webcastIdToDelete}
        isOpen={webcastIdToDelete !== ''}
        onClose={() => setWebcastIdToDelete('')}
      />

      {webcastIdToDuplicate && (
        <DuplicateWebcastModal
          webcastName={webcastNameToDuplicate}
          webcastId={webcastIdToDuplicate}
          onDuplicate={duplicateWebcast}
          onClose={() => {
            setWebcastIdToDuplicate('');
            setWebcastNameToDuplicate('');
          }}
        />
      )}

      {isProgressIndicatorVisible && (
        <DuplicationProgressIndicator
          duplicatedWebcastId={duplicatingInProgressWebcastId}
          duplicatedWebcastName={webcastNameToDuplicate}
          onWebcastDuplicated={() => setDuplicatingInProgressWebcastId('')}
          onWebcastDuplicatedError={handleWebcastDuplicatedError}
        />
      )}
    </>
  );
}
